import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { modal } from "@/lib/modal";
import { apiError } from "@/lib/api";
import { capturarLocalizacao } from "@/lib/geo";
import { ArrowRight, Lock, User, KeyRound, Gauge, Wallet, Sparkles } from "lucide-react";
import logo from "@/assets/logo-grupo.png";

export default function Login() {
  const nav = useNavigate();
  const { loginMaster, loginFrentista } = useAuth();
  const [username, setU] = useState("");
  const [senha, setS] = useState("");
  const [loading, setLoading] = useState(false);
  const geoRef = useRef({});
  const geoReady = useRef(false);

  // Captura a localização ao abrir a tela (pede permissão no navegador).
  useEffect(() => {
    capturarLocalizacao().then((g) => { geoRef.current = g || {}; geoReady.current = true; });
  }, []);

  const focus = (e) => { e.target.style.borderColor = "#0A2A73"; e.target.style.boxShadow = "0 0 0 4px rgba(10,42,115,0.12)"; };
  const blur = (e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = "none"; };
  const inputCls = "w-full h-12 lg:h-[58px] rounded-xl lg:rounded-2xl pl-11 lg:pl-[52px] pr-4 text-base lg:text-[17px] outline-none border-2 border-neutral-200 bg-neutral-50 transition focus:bg-white";

  const goFrentista = (f) => {
    if (f.caixa?.auto_rotated) {
      modal.info(`Olá, ${f.nome}. Caixa anterior excedeu 16h e foi fechado. Novo caixa aberto.`);
      nav("/frentista/atendimento");
    } else if (f.caixa?.retomado) {
      const ms = Date.now() - new Date(f.caixa.aberto_em).getTime();
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
      const tempo = h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m}min`;
      modal.info(`Olá, ${f.nome}. Você tem um caixa aberto há ${tempo}.`);
      nav("/frentista/caixa");
    } else {
      modal.success(`Bem-vindo, ${f.nome}. Caixa novo aberto.`);
      nav("/frentista/atendimento");
    }
  };

  // Perfil detectado dinamicamente: tenta Vendedor (usuário+senha); se não casar, tenta Master (só senha).
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const user = username.trim();
    // Garante a geolocalização (se ainda capturando, espera até ~5s).
    const geo = geoReady.current
      ? geoRef.current
      : await Promise.race([capturarLocalizacao(), new Promise((r) => setTimeout(() => r(geoRef.current), 5000))]);
    try {
      if (user) {
        try {
          const f = await loginFrentista(user, senha, geo);
          return goFrentista(f);
        } catch (errF) {
          const st = errF?.response?.status;
          if (st === 429) { modal.error(apiError(errF)); return; }
          if (st && st !== 401) { modal.error(apiError(errF)); return; }
        }
      }
      try {
        await loginMaster(senha, geo);
        modal.success("Acesso concedido");
        nav("/master/dashboard");
      } catch (errM) {
        const st = errM?.response?.status;
        if (st === 429) { modal.error(apiError(errM)); return; }
        modal.error("Usuário ou senha inválidos.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2"
      style={{
        background:
          "radial-gradient(820px 420px at 85% 8%, rgba(223,31,37,0.22), transparent 60%)," +
          "radial-gradient(720px 400px at 8% 92%, rgba(240,90,40,0.18), transparent 60%)," +
          "radial-gradient(640px 340px at 60% 55%, rgba(22,96,214,0.20), transparent 60%)," +
          "linear-gradient(150deg,#0A2A73 0%,#061A47 100%)",
      }}>
      {/* ---------- PAINEL DE MARCA (desktop) ---------- */}
      <aside className="relative hidden lg:flex flex-col justify-between p-12 xl:p-14 overflow-hidden">
        <div className="bg-white rounded-3xl px-9 py-7 shadow-xl w-fit">
          <img src={logo} alt="Grupo Laçador" className="h-32 xl:h-40 w-auto" data-testid="brand-logo" />
        </div>

        <div className="text-white max-w-lg">
          <div className="text-[11px] tracking-[0.28em] text-white/55 uppercase mb-3">Plataforma de Fidelidade</div>
          <h1 className="font-display font-black uppercase leading-[0.95]" style={{ fontSize: "clamp(2.6rem,3.4vw,3.8rem)" }}>
            Abasteça com<br /><span style={{ color: "#FF7A4D" }}>vantagens reais.</span>
          </h1>
          <p className="text-white/70 mt-5 text-[15px] leading-relaxed">
            Descontos, cashback e fidelidade automáticos na rede <b className="text-white/90">Posto Laçador</b> e <b className="text-white/90">Santa Clara</b>.
          </p>
          <div className="mt-8 space-y-3">
            {[[Gauge, "Atendimento e descontos automáticos"], [Wallet, "Cashback e prêmios de fidelidade"], [Sparkles, "Indicação e campanhas inteligentes"]].map(([Ico, txt], i) => (
              <div key={i} className="flex items-center gap-3 text-white/85 text-sm">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center border border-white/12"><Ico size={15} /></div>
                {txt}
              </div>
            ))}
          </div>
        </div>

        <div className="text-white/40 text-[11px]">© Grupo Laçador · Porto Alegre + Pelotas · RS</div>
      </aside>

      {/* ---------- PAINEL DE LOGIN ---------- */}
      <main className="min-h-screen flex items-center justify-center px-5 py-10 lg:bg-white">
        <div className="w-full max-w-md lg:max-w-xl float-in">
          <div className="bg-white rounded-[28px] shadow-2xl px-7 py-9 md:px-9
                          lg:bg-transparent lg:shadow-none lg:rounded-none lg:p-0">
            {/* logo no topo — apenas mobile */}
            <div className="lg:hidden flex justify-center mb-7">
              <img src={logo} alt="Grupo Laçador" className="h-36 w-auto" data-testid="login-logo" />
            </div>

            <div className="text-center lg:text-left mb-7 lg:mb-9">
              <div className="text-[11px] lg:text-[12px] tracking-[0.24em] uppercase text-[color:var(--brand-red)] font-bold mb-1.5">Plataforma de Fidelidade</div>
              <h2 className="font-display font-black uppercase text-4xl md:text-[2.7rem] lg:text-[3.4rem] leading-none text-[color:var(--ink)]">Acesse a Plataforma</h2>
              <p className="text-sm lg:text-[15px] text-neutral-500 mt-2.5 lg:mt-3.5">Informe suas credenciais para entrar.</p>
            </div>

            <form onSubmit={submit} className="space-y-4 lg:space-y-5">
              <div>
                <label className="text-[11px] lg:text-[12px] uppercase tracking-widest text-neutral-500 font-semibold">Usuário</label>
                <div className="relative mt-2">
                  <User size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input data-testid="login-username" value={username} onChange={(e) => setU(e.target.value)}
                         className={inputCls} onFocus={focus} onBlur={blur} autoComplete="username" autoFocus
                         placeholder="seu usuário (vendedor)" />
                </div>
              </div>
              <div>
                <label className="text-[11px] lg:text-[12px] uppercase tracking-widest text-neutral-500 font-semibold">Senha</label>
                <div className="relative mt-2">
                  <KeyRound size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input data-testid="login-password" type="password" value={senha} onChange={(e) => setS(e.target.value)}
                         className={inputCls} onFocus={focus} onBlur={blur} autoComplete="current-password" required
                         placeholder="••••••••" />
                </div>
              </div>

              <button data-testid="login-submit" type="submit" disabled={loading}
                      className="w-full mt-1 h-[52px] lg:h-[60px] rounded-xl lg:rounded-2xl text-white font-display font-bold uppercase tracking-wide text-[15px] lg:text-[17px] flex items-center justify-center gap-2 transition disabled:opacity-50"
                      style={{ background: "linear-gradient(135deg,#0A2A73,#1660D6)", boxShadow: "0 16px 32px -12px rgba(10,42,115,0.5)" }}>
                {loading ? "Entrando..." : (<>Entrar <ArrowRight size={18} /></>)}
              </button>
            </form>

            <p className="mt-6 text-center lg:text-left text-[11.5px] text-neutral-400 flex items-center justify-center lg:justify-start gap-1.5">
              <Lock size={12} /> Conexão segura · o sistema identifica seu perfil automaticamente.
            </p>

            <div className="mt-5 text-center lg:text-left">
              <a href="https://www.grupolacador.com.br" className="text-[12px] text-neutral-400 hover:text-[color:var(--brand-blue)] transition" data-testid="back-site">← Voltar ao site institucional</a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
